import { useMemo } from "react";
import { AnyPeriod, castPrice, Price, toGQLPrice } from "@ct-react/core";
import { AgeCategory, CartItemOptionInput, CartOptionType, CartPrice, TouristTaxCartOption } from "@shared/graphql";
import { useTools } from "./tools";
import { BookFullPrice } from "@ct-react/calendar";

export const useFakingCartItem = (price: BookFullPrice, period: AnyPeriod, options: CartItemOptionInput[],
                                  guests?: Partial<Record<AgeCategory, number>>, taxConfig?: TouristTaxCartOption,
                                  downPaymentRate?: number) => {

  const { format } = useTools();

  const taxes = useMemo(() => {
    if (!taxConfig || !guests || Object.values(guests).reduce((acc, cur) => acc + cur, 0) < 1)
      return undefined;

    const participantsCount = Object.entries(guests)
                                    .filter(([ _, number ]) => number > 0)
                                    .filter(([ ageCategory ]) => taxConfig.grid.find(item => item.ageCategory === ageCategory))
                                    .map(([ ageCategory, number ]) => ({ ageCategory: ageCategory as AgeCategory, number }));

    const price = participantsCount.reduce((acc, cur) => {
      const base = castPrice(taxConfig.grid.find(item => item.ageCategory === cur.ageCategory)!.price);
      return {
        amount: (acc.amount || 0) + base.amount * cur.number * format.nights(period),
        currency: base.currency
      };
    }, {} as Price);

    return {
      optionId: taxConfig.optionId,
      price: toGQLPrice(price),
      participantsCount
    };
  }, [ period, guests, taxConfig ]);

  const fakeCartItem = useMemo(() => {
    const { bare, discount, providerCharge } = price.detailed;
    const benefits = options.length > 0
      ? { type: CartOptionType.BENEFIT, amount: format.sumPrices(options.map(opt => opt.price)) }
      : undefined;

    const fakeOptions = [
      ...(!!benefits ? [ benefits ] : []),
      ...(!!taxes ? [ { type: CartOptionType.TOURIST_TAX, amount: taxes.price } ] : [])
    ];

    return {
      period,
      price: {
        bare,
        discount,
        options: fakeOptions,
        amount: format.sumPrices([ bare, ...fakeOptions.map(fo => fo.amount) ])
      },
      downPayment: {
        rate: downPaymentRate || 1,
        ...(!!providerCharge) && { providerCharge: providerCharge.amount },
      }
    };

  }, [ price, period, taxes, options ]);

  return { taxes, fakeCartItem };

}
